import {
  Stack,
  Dialog,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { ListingMatchFavoriteListings } from "./ListingMatchFavoriteListings";
import { preventBubbling } from "utils/listings/helper";
import { ListingMatchFavoriteControls } from "./ListingMatchContactListingsBottomOverlay";
import { PageViewStats } from "./ListingPageViewIndicator";
import { useListingPageViews } from "hooks/useListingPageViews";
import type { FavoriteListingsOfContact, RealEstateContact } from "../../../@types/sharedSchema";

type ListingMatchFavoriteListEditDialog = {
  open: boolean;
  contactId?: string;
  contactData: RealEstateContact | null;
  favoriteListingsOfContact: FavoriteListingsOfContact;
  onClose: () => void;
};

function ListingsMatchFavoriteListEditDialog({
  open,
  contactId,
  contactData,
  favoriteListingsOfContact,
  onClose
}: ListingMatchFavoriteListEditDialog) {
  const { t: translate } = useTranslation();

  const { viewData: pageViewData } = useListingPageViews(favoriteListingsOfContact?.id);

  // NOTE: DialogTitle/DialogContent must stay DIRECT children of the Dialog.
  // The paper is a column flexbox and DialogContent only becomes the scroll
  // container (constrained height + overflow) as a direct flex child. A
  // wrapper element breaks that, and with the theme's
  // `overscroll-behavior: contain` on DialogContent, wheel/touch scrolling
  // dies entirely (or leaks to the page behind the modal). Click containment
  // (this dialog is rendered inside a clickable table row, and portal clicks
  // bubble through the React tree) lives on the Dialog itself instead.
  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={preventBubbling(onClose)}
      onClick={preventBubbling(null, false)}
      PaperProps={{ sx: { height: "90vh" } }}
    >
      <DialogTitle sx={{ py: 2 }}>
        <Stack gap={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">
              {translate("listingsMatch.favoriteListingsEdit.title")}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2}>
            <PageViewStats viewData={pageViewData} />
            <ListingMatchFavoriteControls
              minisiteId={favoriteListingsOfContact?.id}
              disabled={!favoriteListingsOfContact?.listingIds?.length}
              phoneNumber={favoriteListingsOfContact?.contactId}
            />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <ListingMatchFavoriteListings
          hideControls
          showSortBy
          contactId={contactId}
          contactData={contactData}
          favoriteListingsOfContact={favoriteListingsOfContact}
        />
      </DialogContent>
    </Dialog>
  );
}

export default ListingsMatchFavoriteListEditDialog;
